import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { JourneyProgress } from "@/components/JourneyProgress";
import { fetchJournalEntries, sortNewestFirst, JOURNAL_FALLBACK } from "@/lib/journal";

export default function JournalArchive() {
  const [entries, setEntries] = useState(() => sortNewestFirst(JOURNAL_FALLBACK));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "ROADRIG · Journal Archive";
    let mounted = true;
    fetchJournalEntries()
      .then((data) => { if (mounted && Array.isArray(data) && data.length) setEntries(sortNewestFirst(data)); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("en-AU", { year: "numeric", month: "long", day: "numeric" });
    } catch { return ""; }
  };

  return (
    <div data-testid="journal-archive-page" className="relative bg-[#0A0A0C] text-white min-h-screen">
      <Navbar />

      <section className="relative pt-36 md:pt-44 pb-16 md:pb-20 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Link to="/" data-testid="archive-back-link" className="serial text-[10px] text-white/50 hover:text-white inline-flex items-center gap-2 mb-8">
            <ArrowLeft size={12} /> Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-5">
            <span className="section-mark" />
            <span className="overline">Founder&apos;s Journal · Full Archive</span>
          </div>
          <h1 className="h-display text-white text-5xl sm:text-7xl lg:text-[7rem]">
            The Build, <br />
            <span className="text-[#F47A20]">documented.</span>
          </h1>
          <p className="mt-8 text-white/65 max-w-2xl text-base sm:text-lg">
            Every entry from the workshop floor. Photos, videos, the wins and the misses. The full ROADRIG OUTLAW V1 build, in order.
          </p>
          <div className="mt-10 serial text-[10px] text-white/40" data-testid="archive-count">
            {loading ? "LOADING…" : `${entries.length} ${entries.length === 1 ? "entry" : "entries"} · newest first`}
          </div>

          <div className="mt-10">
            <JourneyProgress />
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 space-y-12 md:space-y-16">
          {entries.map((e, i) => (
            <motion.article
              key={e.id || e.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.6 }}
              data-testid={`archive-entry-${i}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-start"
            >
              <div className="md:col-span-5">
                <Link to={`/journal/${e.slug}`} data-testid={`archive-entry-${i}-image`} className="block group">
                  <div className="relative aspect-[4/3] md:aspect-[4/5] overflow-hidden rounded-sm border border-white/10 group-hover:border-[#F47A20]/55 transition-colors bg-[#141417]">
                    {e.video_url ? (
                      <video controls preload="none" poster={e.image_url} className="w-full h-full object-cover">
                        <source src={e.video_url} />
                      </video>
                    ) : (
                      <img
                        src={e.image_url}
                        alt={e.date_label}
                        style={{ objectPosition: e.object_pos || "50% 50%" }}
                        className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.03]"
                      />
                    )}
                    <div className="absolute top-3 left-3 tactical-badge text-[9px]">{e.tag}</div>
                  </div>
                </Link>
              </div>
              <div className="md:col-span-7 md:pt-4">
                <div className="flex items-center gap-3 mb-4 serial text-[10px] text-white/50">
                  <span className="text-[#F47A20]">{(e.date_label || "").toUpperCase()}</span>
                  <span className="w-1 h-1 rounded-full bg-white/30" />
                  <span className="inline-flex items-center gap-1.5"><Calendar size={11} /> {fmtDate(e.published_at)}</span>
                </div>
                <Link to={`/journal/${e.slug}`} data-testid={`archive-entry-${i}-link`} className="group inline-block">
                  <h2 className="h-display text-3xl md:text-5xl text-white leading-tight mb-5 group-hover:text-[#F47A20] transition-colors">{e.title}</h2>
                </Link>
                <p className="text-white/70 text-base md:text-lg leading-relaxed whitespace-pre-line">{e.body}</p>
                <Link
                  to={`/journal/${e.slug}`}
                  data-testid={`archive-entry-${i}-cta`}
                  className="mt-6 inline-flex items-center gap-2 serial text-[10px] tracking-[0.25em] text-[#F47A20] font-bold hover:gap-3 transition-all"
                >
                  Read the full entry <ArrowRight size={12} />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
